import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  LinearProgress,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormLabel,
  IconButton,
  Stack,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Link,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import MenuIcon from '@mui/icons-material/Menu';
import { CancerType, UserType } from '../schema';

// Import form schemas
import breastSchema from '../schema/breast.json';
import lungSchema from '../schema/lung.json';
import { CaseInputType, useCaseIntake } from '../context/CaseIntakeContext';
import { useAuthContext } from '../context/AuthContext';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { usePersistentNavigate } from '../utils/navigation';

// Type definitions
interface SchemaField {
  id: string;
  label: string;
  type: string;
  options?: string[];
  fields?: SchemaField[];
}

interface SchemaSection {
  section: string;
  fields: SchemaField[];
}

// Cancer type selection component
const CancerTypeSelector = ({ onSelect, selectedType }: { onSelect: (type: string) => void, selectedType: string | null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = usePersistentNavigate();

  return (
    <Box sx={{ py: { xs: 2, sm: 3 } }}>
      {/* New disclaimer alert */}
      <Alert
        severity="info"
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          '& .MuiAlert-message': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Form Completion Guidance
        </Typography>
        <Typography variant="body2">
          All fields in this form are optional; simply fill out as much information as you already know about your case.
          If certain details are unknown or not applicable, skip those fields and leave them blank.
          However, providing as much information as possible will help us assist you better.
        </Typography>
      </Alert>

      <Typography variant="h6" sx={{
        mb: 2,
        color: '#2c5282',
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        fontWeight: 500
      }}>
        Select Cancer Type
      </Typography>

      {/* New explanatory text */}
      <Typography variant="body2" sx={{ mb: 4, color: '#4a5568' }}>
        Please select a cancer type from the supported options below. If the type you need is
        not listed, please use one of the <Link
          component="button"
          onClick={() => navigate('/')}
          sx={{
            color: '#4299e1',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            // Add these properties to fix vertical alignment:
            padding: 0,
            verticalAlign: 'baseline',
            display: 'inline',
            minWidth: 'unset',
            lineHeight: 'inherit',
            background: 'none',
            border: 'none',
            fontSize: 'inherit'
          }}
        >
          other intake methods
        </Link> until we add more options.
      </Typography>

      {/* Updated vertical stack of buttons */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '400px', // Limit width for better appearance
        mx: 'auto' // Center the buttons
      }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            cursor: 'pointer',
            backgroundColor: selectedType === 'breast' ? '#ebf8ff' : 'white',
            border: selectedType === 'breast' ? '2px solid #4299e1' : '1px solid #e2e8f0',
            '&:hover': { bgcolor: '#f0f7ff', transform: 'translateY(-2px)', boxShadow: '0 4px 6px rgba(66, 153, 225, 0.1)' },
            textAlign: 'center',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60px'
          }}
          onClick={() => onSelect('breast')}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: '#2c5282' }}>
            Breast Cancer
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            cursor: 'pointer',
            backgroundColor: selectedType === 'lung' ? '#ebf8ff' : 'white',
            border: selectedType === 'lung' ? '2px solid #4299e1' : '1px solid #e2e8f0',
            '&:hover': { bgcolor: '#f0f7ff', transform: 'translateY(-2px)', boxShadow: '0 4px 6px rgba(66, 153, 225, 0.1)' },
            textAlign: 'center',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60px'
          }}
          onClick={() => onSelect('lung')}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: '#2c5282' }}>
            Lung Cancer
          </Typography>
        </Paper>

        {/* New greyed out button for coming soon */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            backgroundColor: '#f7fafc',
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60px',
            opacity: 0.7,
            cursor: 'not-allowed'
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: '#718096' }}>
            More Cancer Types (Coming Soon)
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

// Repeatable field component for radiation and systemic therapy
const RepeatableField = ({
  field,
  formData,
  sectionName,
  updateFormData
}: {
  field: SchemaField,
  formData: any,
  sectionName: string,
  updateFormData: (callback: (prev: any) => any) => void
}) => {
  // Initialize array if it doesn't exist
  const entries = formData[sectionName]?.[field.id] || [];

  const handleAddEntry = () => {
    const newEntry: Record<string, any> = {};
    // Initialize all subfields with empty values
    if (field.fields) {
      field.fields.forEach(subField => {
        if (subField.type === 'multi-check') {
          newEntry[subField.id] = [];
        } else {
          newEntry[subField.id] = '';
        }
      });
    }

    updateFormData(prevData => ({
      ...prevData,
      [sectionName]: {
        ...prevData[sectionName],
        [field.id]: [...entries, newEntry]
      }
    }));
  };

  const handleRemoveEntry = (index: number) => {
    const updatedEntries = [...entries];
    updatedEntries.splice(index, 1);

    updateFormData(prevData => ({
      ...prevData,
      [sectionName]: {
        ...prevData[sectionName],
        [field.id]: updatedEntries
      }
    }));
  };

  const handleEntryFieldChange = (entryIndex: number, subFieldId: string, value: any) => {
    const updatedEntries = [...entries];
    updatedEntries[entryIndex] = {
      ...updatedEntries[entryIndex],
      [subFieldId]: value
    };

    updateFormData(prevData => ({
      ...prevData,
      [sectionName]: {
        ...prevData[sectionName],
        [field.id]: updatedEntries
      }
    }));
  };

  const handleMultiCheckChange = (entryIndex: number, subFieldId: string, option: string, checked: boolean) => {
    const currentValues = entries[entryIndex]?.[subFieldId] || [];

    let newValues: string[];
    if (checked) {
      newValues = [...currentValues, option];
    } else {
      newValues = currentValues.filter((item: string) => item !== option);
    }

    const updatedEntries = [...entries];
    updatedEntries[entryIndex] = {
      ...updatedEntries[entryIndex],
      [subFieldId]: newValues
    };

    updateFormData(prevData => ({
      ...prevData,
      [sectionName]: {
        ...prevData[sectionName],
        [field.id]: updatedEntries
      }
    }));
  };

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 1, sm: 2 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <FormLabel sx={{
          fontWeight: 'bold',
          color: '#2c5282',
          fontSize: { xs: '0.9rem', sm: '1rem' }
        }}>
          {field.label}
        </FormLabel>
        <Button
          startIcon={<AddCircleIcon />}
          variant="outlined"
          size="small"
          onClick={handleAddEntry}
          sx={{
            ml: { xs: 0, sm: 2 },
            color: '#4299e1',
            borderColor: '#4299e1',
            '&:hover': {
              borderColor: '#2c5282',
              color: '#2c5282'
            }
          }}
        >
          Add Entry
        </Button>
      </Box>

      {entries.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{
          fontStyle: 'italic',
          mb: 2,
          fontSize: { xs: '0.8rem', sm: '0.875rem' }
        }}>
          No entries added yet. Click "Add Entry" to begin.
        </Typography>
      )}

      {(entries as Array<Record<string, any>>).map((entry: Record<string, any>, entryIndex: number) => (
        <Card key={entryIndex} variant="outlined" sx={{
          mb: 2,
          position: 'relative',
          border: '1px solid #e2e8f0',
          borderRadius: '8px'
        }}>
          <IconButton
            size="small"
            color="error"
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => handleRemoveEntry(entryIndex)}
          >
            <DeleteIcon />
          </IconButton>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{
              mb: 2,
              color: '#4299e1',
              fontSize: { xs: '0.85rem', sm: '0.95rem' }
            }}>
              Entry #{entryIndex + 1}
            </Typography>

            {field.fields?.map((subField: SchemaField) => (
              <Box key={subField.id} sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <FormLabel sx={{
                    mb: 1,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    color: '#4a5568'
                  }}>
                    {subField.label}
                  </FormLabel>

                  {subField.type === 'text' && (
                    <TextField
                      size="small"
                      fullWidth
                      value={entry[subField.id] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEntryFieldChange(entryIndex, subField.id, e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#f7fafc',
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    />
                  )}

                  {subField.type === 'radio' && subField.options && (
                    <RadioGroup
                      value={entry[subField.id] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEntryFieldChange(entryIndex, subField.id, e.target.value)}
                      row
                    >
                      {subField.options.map((option: string) => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          control={
                            <Radio
                              size="small"
                              onClick={(e) => {
                                // If this radio is already selected, unselect it
                                if (entry[subField.id] === option) {
                                  e.preventDefault();
                                  handleEntryFieldChange(entryIndex, subField.id, '');
                                }
                              }}
                              sx={{
                                color: '#a0aec0',
                                '&.Mui-checked': {
                                  color: '#4299e1',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {option}
                            </Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  )}

                  {subField.type === 'multi-check' && subField.options && (
                    <FormGroup row>
                      {subField.options.map((option: string) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              checked={((entry[subField.id] || []) as string[]).includes(option)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMultiCheckChange(entryIndex, subField.id, option, e.target.checked)}
                              size="small"
                              sx={{
                                color: '#a0aec0',
                                '&.Mui-checked': {
                                  color: '#4299e1',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {option}
                            </Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}

                  {subField.type === 'month' && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        views={['year', 'month']}
                        value={entry[subField.id] ? dayjs(entry[subField.id]) : null}
                        onChange={(newValue) => {
                          // Format as YYYY-MM
                          const formattedValue = newValue ? newValue.format('YYYY-MM') : '';
                          handleEntryFieldChange(entryIndex, subField.id, formattedValue);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#f7fafc',
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </FormControl>
              </Box>
            ))}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

// Update the GroupField component to properly render all field types
const GroupField = ({
  field,
  formData,
  sectionName,
  updateFormData
}: {
  field: SchemaField,
  formData: any,
  sectionName: string,
  updateFormData: (callback: (prev: any) => any) => void
}) => {
  const theme = useTheme();

  // Helper function to handle change for nested fields
  const handleNestedChange = (fieldId: string, value: any) => {
    updateFormData(prevData => {
      const currentSection = prevData[sectionName] || {};
      // Make sure the group field exists
      const currentGroup = currentSection[field.id] || {};

      return {
        ...prevData,
        [sectionName]: {
          ...currentSection,
          [field.id]: {
            ...currentGroup,
            [fieldId]: value
          }
        }
      };
    });
  };

  // Helper function to render the appropriate input for each field type
  const renderFieldInput = (childField: SchemaField) => {
    const value = formData[sectionName]?.[field.id]?.[childField.id] || '';

    // Special handling for biopsy site table
    if (field.id === 'biopsy_results' && childField.id === 'biopsy_site') {
      // Initialize the biopsy results object if not already done
      const biopsyResults = value || {};

      return (
        <TableContainer component={Paper} variant="outlined" sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          mb: 2
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f7fafc' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '60%' }}>Biopsy Site</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>Positive</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>Negative</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {childField.options?.map((site: string) => (
                <TableRow key={site} sx={{ '&:hover': { bgcolor: '#f7fafc' } }}>
                  <TableCell component="th" scope="row" sx={{
                    pl: 2,
                    fontSize: { xs: '0.875rem', sm: '0.95rem' }
                  }}>
                    {site}
                  </TableCell>
                  <TableCell align="center">
                    <Radio
                      size="small"
                      checked={biopsyResults[site] === 'Positive'}
                      onChange={() => {
                        const updatedResults = { ...biopsyResults };
                        updatedResults[site] = 'Positive';
                        handleNestedChange(childField.id, updatedResults);
                      }}
                      onClick={(e) => {
                        // If this radio is already selected, unselect it
                        if (biopsyResults[site] === 'Positive') {
                          e.preventDefault();
                          const updatedResults = { ...biopsyResults };
                          delete updatedResults[site];
                          handleNestedChange(childField.id, updatedResults);
                        }
                      }}
                      sx={{
                        color: '#a0aec0',
                        '&.Mui-checked': { color: '#4299e1' },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Radio
                      size="small"
                      checked={biopsyResults[site] === 'Negative'}
                      onChange={() => {
                        const updatedResults = { ...biopsyResults };
                        updatedResults[site] = 'Negative';
                        handleNestedChange(childField.id, updatedResults);
                      }}
                      onClick={(e) => {
                        // If this radio is already selected, unselect it
                        if (biopsyResults[site] === 'Negative') {
                          e.preventDefault();
                          const updatedResults = { ...biopsyResults };
                          delete updatedResults[site];
                          handleNestedChange(childField.id, updatedResults);
                        }
                      }}
                      sx={{
                        color: '#a0aec0',
                        '&.Mui-checked': { color: '#4299e1' },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    switch (childField.type) {
      case 'text':
      case 'number':
        return (
          <TextField
            type={childField.type}
            value={value}
            onChange={(e) => handleNestedChange(childField.id, e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f7fafc',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          />
        );

      case 'select':
        return (
          <Select
            value={value}
            onChange={(e) => handleNestedChange(childField.id, e.target.value)}
            fullWidth
            size="small"
            displayEmpty
            sx={{
              bgcolor: '#f7fafc',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            <MenuItem value="" disabled><em>Select an option</em></MenuItem>
            {childField.options?.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );

      case 'radio':
        return (
          <RadioGroup
            row
            value={value}
            onChange={(e) => handleNestedChange(childField.id, e.target.value)}
          >
            {childField.options?.map(option => (
              <FormControlLabel
                key={option}
                value={option}
                control={
                  <Radio
                    size="small"
                    onClick={(e) => {
                      // If this radio is already selected, unselect it
                      if (value === option) {
                        e.preventDefault();
                        handleNestedChange(childField.id, '');
                      }
                    }}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': {
                        color: '#4299e1',
                      },
                    }}
                  />
                }
                label={option}
                sx={{
                  mr: 3,
                  mb: 0.5,
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            ))}
          </RadioGroup>
        );

      // Add new renderers for textarea and multi-check fields
      case 'textarea':
        return (
          <TextField
            fullWidth
            id={childField.id}
            multiline
            rows={4}
            value={value || ''}
            onChange={(e) => handleNestedChange(childField.id, e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f7fafc',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          />
        );

      // Modified case to handle both multi-check and checkboxes types
      case 'multi-check':
      case 'checkboxes':
        return (
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormGroup>
              {childField.options?.map(option => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={value.includes(option)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleNestedChange(childField.id, [...value, option]);
                        } else {
                          handleNestedChange(childField.id, value.filter((val: string) => val !== option));
                        }
                      }}
                      sx={{
                        color: '#a0aec0',
                        '&.Mui-checked': { color: '#4299e1' },
                      }}
                    />
                  }
                  label={option}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: '#4a5568',
                      fontSize: '0.95rem'
                    }
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      default:
        return <Typography color="error">Unknown field type: {childField.type}</Typography>;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{
        mb: 1.5,
        color: '#2c5282',
        fontSize: { xs: '1rem', sm: '1.1rem' },
        fontWeight: 500
      }}>
        {field.label}
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ml: { xs: 1, sm: 2 }
      }}>
        {field.fields?.map((childField) => (
          <Box key={childField.id} sx={{ mb: 1.5 }}>
            {/* Hide label for biopsy site table */}
            {!(field.id === 'biopsy_results' && childField.id === 'biopsy_site') && (
              <FormLabel sx={{
                fontSize: '0.9rem',
                color: '#4a5568',
                mb: 0.5,
                display: 'block'
              }}>
                {childField.label}
              </FormLabel>
            )}
            {renderFieldInput(childField)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Form section component
const FormSection = ({
  section,
  formData,
  updateFormData
}: {
  section: SchemaSection,
  formData: any,
  updateFormData: (callback: (prev: any) => any) => void
}) => {
  const handleChange = (fieldId: string, value: any) => {
    updateFormData(prevData => ({
      ...prevData,
      [section.section]: {
        ...prevData[section.section],
        [fieldId]: value
      }
    }));
  };

  const handleMultiCheckChange = (fieldId: string, option: string, checked: boolean) => {
    const currentValues = formData[section.section]?.[fieldId] || [];

    let newValues: string[];
    if (checked) {
      newValues = [...currentValues, option];
    } else {
      newValues = currentValues.filter((item: string) => item !== option);
    }

    updateFormData(prevData => ({
      ...prevData,
      [section.section]: {
        ...prevData[section.section],
        [fieldId]: newValues
      }
    }));
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      <Typography variant="h6" sx={{
        mb: { xs: 2, sm: 3 },
        color: '#2c5282',
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        fontWeight: 500
      }}>
        {section.section}
      </Typography>

      {section.fields.map((field) => (
        <Box key={field.id} sx={{ mb: { xs: 2, sm: 3 } }}>
          {field.type === 'repeatable' ? (
            <RepeatableField
              field={field}
              formData={formData}
              sectionName={section.section}
              updateFormData={updateFormData}
            />
          ) : field.type === 'group' ? (
            <GroupField
              field={field}
              formData={formData}
              sectionName={section.section}
              updateFormData={updateFormData}
            />
          ) : (
            <FormControl fullWidth sx={{ mb: { xs: 1, sm: 2 } }}>
              <FormLabel sx={{
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}>
                {field.label}
              </FormLabel>

              {field.type === 'text' && (
                <TextField
                  value={formData[section.section]?.[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f7fafc',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              )}

              {field.type === 'textarea' && (
                <TextField
                  value={formData[section.section]?.[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f7fafc',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              )}

              {field.type === 'number' && (
                <TextField
                  type="number"
                  value={formData[section.section]?.[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f7fafc',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              )}

              {field.type === 'radio' && field.options && (
                <RadioGroup
                  value={formData[section.section]?.[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                >
                  {field.options.map(option => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={
                        <Radio
                          size="small"
                          onClick={(e) => {
                            // If this radio is already selected, unselect it
                            if (formData[section.section]?.[field.id] === option) {
                              e.preventDefault();
                              handleChange(field.id, '');
                            }
                          }}
                          sx={{
                            color: '#a0aec0',
                            '&.Mui-checked': {
                              color: '#4299e1',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {option}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              )}

              {field.type === 'select' && field.options && (
                <FormControl fullWidth size="small">
                  <Select
                    value={formData[section.section]?.[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    displayEmpty
                    sx={{
                      bgcolor: '#f7fafc',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {field.options.map(option => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {['multi-check', 'checkboxes'].includes(field.type) && field.options && (
                <FormGroup>
                  {field.options.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={(formData[section.section]?.[field.id] || []).includes(option)}
                          onChange={(e) => handleMultiCheckChange(field.id, option, e.target.checked)}
                          size="small"
                          sx={{
                            color: '#a0aec0',
                            '&.Mui-checked': {
                              color: '#4299e1',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {option}
                        </Typography>
                      }
                    />
                  ))}
                </FormGroup>
              )}
            </FormControl>
          )}
        </Box>
      ))}
    </Box>
  );
};

// New Review component to show summary and missing fields
const FormReview = ({
  formData,
  formSchema,
  cancerType,
  onNavigateToSection
}: {
  formData: any,
  formSchema: SchemaSection[] | null,
  cancerType: string | null,
  onNavigateToSection: (index: number) => void
}) => {
  const missingFields: { section: string, field: string, index: number }[] = [];

  if (formSchema) {
    formSchema.forEach((section, sectionIndex) => {
      section.fields.forEach(field => {
        // Skip fields that might conflict with the cancer type selection
        if (field.id === "cancer_type" || field.id === "type" && section.section === "Cancer Type") {
          return;
        }

        // Skip textarea fields as they're considered optional
        if (field.type === 'textarea') {
          return;
        }

        // Skip repeatable fields entirely
        if (field.type === 'repeatable') {
          return;
        }

        const sectionData = formData[section.section] || {};

        // Check if any field is empty
        if (field.type !== 'repeatable' &&
          (sectionData[field.id] === undefined ||
            sectionData[field.id] === null ||
            sectionData[field.id] === '' ||
            (Array.isArray(sectionData[field.id]) && sectionData[field.id].length === 0))) {
          missingFields.push({
            section: section.section,
            field: field.label,
            index: sectionIndex + 1 // +1 because first step is cancer type
          });
        }
        // Special handling for group fields
        else if (field.type === 'group' && field.fields) {
          const groupData = sectionData[field.id] || {}; // Get the group data correctly

          field.fields.forEach(subField => {
            // Skip textarea subfields in groups
            if (subField.type === 'textarea') {
              return;
            }

            // Check the nested field value correctly
            const subFieldValue = groupData[subField.id];

            if (subFieldValue === undefined ||
              subFieldValue === null ||
              subFieldValue === '' ||
              (Array.isArray(subFieldValue) && subFieldValue.length === 0)) {
              missingFields.push({
                section: section.section,
                field: `${field.label}: ${subField.label}`,
                index: sectionIndex + 1
              });
            }
          });
        }
      });
    });
  }

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      <Typography variant="h6" sx={{
        mb: { xs: 2, sm: 3 },
        color: '#2c5282',
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        fontWeight: 500
      }}>
        Review Your Case Information
      </Typography>

      {missingFields.length > 0 && (
        <Alert
          severity="warning"
          sx={{
            mb: { xs: 3, sm: 4 },
            '& .MuiAlert-message': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        >
          There are {missingFields.length} empty fields in your form. While you can submit with missing information,
          providing complete details will help our reviewers better understand your case.
        </Alert>
      )}

      {/* Missing fields table */}
      {missingFields.length > 0 && (
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography variant="subtitle1" sx={{
            mb: 2,
            fontWeight: 'bold',
            color: '#2c5282',
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}>
            Missing Information
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                padding: { xs: '8px 6px', sm: '16px' }
              }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell width="30%">Section</TableCell>
                  <TableCell width="50%">Field</TableCell>
                  <TableCell width="20%">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingFields.map((item, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f7fafc' } }}>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      {item.section}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      {item.field}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={() => onNavigateToSection(item.index)}
                        sx={{
                          color: '#4299e1',
                          textTransform: 'none',
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          p: { xs: '2px 4px', sm: '3px 8px' },
                          minWidth: { xs: '50px', sm: 'auto' },
                          '&:hover': {
                            color: '#2c5282'
                          }
                        }}
                      >
                        Go to
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Form data summary */}
      <Typography variant="subtitle1" sx={{
        mb: 2,
        fontWeight: 'bold',
        color: '#2c5282',
        fontSize: { xs: '0.9rem', sm: '1rem' }
      }}>
        Case Summary
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{
              color: '#4299e1',
              fontWeight: 500,
              fontSize: { xs: '0.85rem', sm: '0.95rem' }
            }}>
              Cancer Type
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {cancerType === 'breast' ? 'Breast Cancer' : 'Lung Cancer'}
            </Typography>
          </CardContent>
        </Card>

        {formSchema && formSchema.map((section, idx) => (
          <Card key={idx} variant="outlined" sx={{
            mb: 2,
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                flexWrap: 'wrap'
              }}>
                <Typography variant="subtitle2" sx={{
                  color: '#4299e1',
                  fontWeight: 500,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  mb: { xs: 1, sm: 0 }
                }}>
                  {section.section}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onNavigateToSection(idx + 1)}
                  sx={{
                    color: '#4299e1',
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    minWidth: { xs: '60px', sm: 'auto' },
                    padding: { xs: '2px 8px', sm: '3px 9px' },
                    '&:hover': {
                      color: '#2c5282'
                    }
                  }}
                >
                  Edit
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {section.fields.map(field => {
                const value = formData[section.section]?.[field.id];

                // Skip displaying empty values
                if (value === undefined || value === null || value === '' ||
                  (Array.isArray(value) && value.length === 0)) {
                  return null;
                }

                return (
                  <Box key={field.id} sx={{ mb: 1 }}>
                    <Typography variant="body2" component="span" sx={{
                      fontWeight: 'bold',
                      mr: 1,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                      {field.label}:
                    </Typography>

                    {field.type === 'repeatable' ? (
                      <Box sx={{ mt: 1, ml: 2 }}>
                        {Array.isArray(value) && value.map((entry: any, entryIndex: number) => (
                          <Box key={entryIndex} sx={{
                            mb: 1.5,
                            pl: 2,
                            pb: 1.5,
                            borderBottom: entryIndex < value.length - 1 ? '1px dashed #e2e8f0' : 'none'
                          }}>
                            <Typography variant="caption" sx={{
                              fontWeight: 500,
                              display: 'block',
                              mb: 0.5,
                              color: '#4299e1'
                            }}>
                              Entry #{entryIndex + 1}
                            </Typography>

                            {field.fields?.map(subField => {
                              const subValue = entry[subField.id];

                              // Skip empty subfield values
                              if (subValue === undefined || subValue === null || subValue === '' ||
                                (Array.isArray(subValue) && subValue.length === 0)) {
                                return null;
                              }

                              return (
                                <Box key={subField.id} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2" component="span" sx={{
                                    fontWeight: 'bold',
                                    mr: 1,
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    color: '#4a5568'
                                  }}>
                                    {subField.label}:
                                  </Typography>
                                  <Typography variant="body2" component="span" sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                  }}>
                                    {Array.isArray(subValue) ? subValue.join(', ') : subValue.toString()}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    ) : field.type === 'group' ? (
                      // Special handling for group fields
                      <Box sx={{ mt: 1, ml: 2 }}>
                        {field.fields?.map(subField => {
                          const subValue = value[subField.id];

                          // Skip empty subfield values
                          if (subValue === undefined || subValue === null || subValue === '' ||
                            (Array.isArray(subValue) && subValue.length === 0)) {
                            return null;
                          }

                          return (
                            <Box key={subField.id} sx={{ mb: 0.5 }}>
                              <Typography variant="body2" component="span" sx={{
                                fontWeight: 'bold',
                                mr: 1,
                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                color: '#4a5568'
                              }}>
                                {subField.label}:
                              </Typography>
                              <Typography variant="body2" component="span" sx={{
                                fontSize: { xs: '0.75rem', sm: '0.8rem' }
                              }}>
                                {Array.isArray(subValue) ? subValue.join(', ') : subValue.toString()}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : Array.isArray(value) ? (
                      <Typography variant="body2" component="span" sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}>
                        {value.join(', ')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" component="span" sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}>
                        {value.toString()}
                      </Typography>
                    )}
                  </Box>
                );
              })}

              {!Object.keys(formData[section.section] || {}).length && (
                <Typography variant="body2" color="text.secondary" sx={{
                  fontStyle: 'italic',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}>
                  No information provided
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

const CaseIntakeForm = () => {
  const navigate = usePersistentNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [cancerType, setCancerType] = useState<string | null>(null);
  const [formSchema, setFormSchema] = useState<SchemaSection[] | null>(null);
  const [formData, setFormData] = useState<any>({});
  const { setCaseContextJson, setCaseInputType, setContextCancerType } = useCaseIntake();
  const { accessToken, completedRegistration, userType } = useAuthContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Track visited and completed sections separately
  const [visitedSteps, setVisitedSteps] = useState<number[]>([0]);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  useEffect(() => {
    if (cancerType === 'breast') {
      setFormSchema(breastSchema);
    } else if (cancerType === 'lung') {
      setFormSchema(lungSchema);
    }
  }, [cancerType]);

  const handleNext = () => {
    // Mark current step as completed
    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps(prev => [...prev, activeStep]);
    }

    const nextStep = activeStep + 1;
    setActiveStep(nextStep);

    // Add next step to visited steps
    if (!visitedSteps.includes(nextStep)) {
      setVisitedSteps(prev => [...prev, nextStep]);
    }

    // Scroll to top
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    setActiveStep(prevStep);

    // Scroll to top
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleStepClick = (step: number) => {
    // Allow navigation to any visited step
    if (visitedSteps.includes(step)) {
      setActiveStep(step);

      // Scroll to top
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleSubmit = async () => {
    const cancerTypeEnum = cancerType === 'breast' ? CancerType.BREAST :
      cancerType === 'lung' ? CancerType.THORACIC :
        CancerType.NOT_PROVIDED;

    try {
      setCaseContextJson(JSON.stringify(formData));
      setCaseInputType(CaseInputType.FORM);
      setContextCancerType(cancerTypeEnum);

      // Navigate to a success page or show a success message
      if (accessToken == null) {
        navigate('/login');
        return;
      }

      const isAdmin = userType === UserType.ADMIN;

      if (!completedRegistration && !isAdmin) {
        navigate('/complete-registration');
        return;
      }

      navigate('/case-report');
    } catch (error) {
      console.error("Failed to submit case:", error);
      // Handle submission error, e.g., show an error message to the user
    }
  };

  // Check if all sections are completed
  const areAllStepsCompleted = () => {
    if (!formSchema) return false;

    // Need to complete all steps from 0 to formSchema.length (not including review step)
    for (let i = 0; i <= formSchema.length; i++) {
      if (!completedSteps.includes(i)) {
        return false;
      }
    }
    return true;
  };

  const getStepContent = (step: number) => {
    if (step === 0) {
      return <CancerTypeSelector onSelect={setCancerType} selectedType={cancerType} />;
    }

    if (!formSchema) return null;

    // If we're at the review step (after all form sections)
    if (step === formSchema.length + 1) {
      return (
        <FormReview
          formData={formData}
          formSchema={formSchema}
          cancerType={cancerType}
          onNavigateToSection={handleStepClick}
        />
      );
    }

    const currentSection = formSchema[step - 1];
    return currentSection ? (
      <FormSection
        section={currentSection}
        formData={formData}
        updateFormData={setFormData}
      />
    ) : null;
  };

  // Total steps now includes an additional review step
  const totalSteps = formSchema ? formSchema.length + 2 : 1; // +2 for cancer type and review
  const progress = (completedSteps.length / (totalSteps - 1)) * 100; // Don't count review in progress

  // Determine if we're on the review screen (last step)
  const isReviewStep = formSchema && activeStep === formSchema.length + 1;

  // Group steps for two-row mobile display - ADDING BACK REVIEW STEP
  const getGroupedSteps = () => {
    if (!formSchema) return [[], []];

    // Add cancer type + all schema sections + REVIEW
    const allSteps = [
      { id: 'cancer-type', label: 'Cancer Type' },
      ...formSchema.map((section) => ({ id: section.section, label: section.section })),
      { id: 'review', label: 'Review' } // Add review step back
    ];

    // Split into two rows for mobile
    const midpoint = Math.ceil(allSteps.length / 2);
    return [
      allSteps.slice(0, midpoint),
      allSteps.slice(midpoint)
    ];
  };

  // Custom mobile stepper component - updated to handle review step properly
  const MobileStepper = () => {
    const rows = getGroupedSteps();

    return (
      <Box sx={{ width: '100%', mb: 2 }}>
        {rows.map((rowSteps, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: rowIndex === 0 ? 1.5 : 0
            }}
          >
            {rowSteps.map((step, i) => {
              // Calculate the actual step index
              const stepIndex = rowIndex === 0 ? i : rows[0].length + i;

              // Handle the review step specially
              const isReviewStep = step.id === 'review';
              const actualStepIndex = isReviewStep ? formSchema!.length + 1 : stepIndex;

              const isCompleted = completedSteps.includes(actualStepIndex);
              const isActive = activeStep === actualStepIndex;
              const isVisited = visitedSteps.includes(actualStepIndex);
              const isClickable = isVisited ||
                (isReviewStep && areAllStepsCompleted());

              return (
                <Box
                  key={step.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    cursor: isClickable ? 'pointer' : 'default'
                  }}
                  onClick={() => isClickable ? handleStepClick(actualStepIndex) : null}
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ color: '#4299e1', fontSize: '1.2rem' }} />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{
                        color: isActive ? '#4299e1' :
                          isClickable ? '#a0aec0' : '#e2e8f0',
                        fontSize: '1.2rem'
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
        <Toolbar sx={{
          minHeight: { xs: '56px', sm: '64px' },
          justifyContent: 'space-between',
          padding: { xs: '0 16px', sm: '0 24px' }
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')} // Navigate back to provider landing page
          >
            <Typography variant="h5" component="div" sx={{
              color: '#2c5282',
              fontWeight: 'bold',
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
              display: 'flex'
            }}>
              PRECI<span style={{ color: '#4299e1' }}>SCA</span>
            </Typography>
          </Box>

          {/* Remove all desktop navigation links */}

          {/* Menu icon - show on both mobile and desktop */}
          <IconButton
            color="inherit"
            sx={{
              color: '#2c5282'
              // Removed display condition to show on both mobile and desktop
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container sx={{
        mt: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 3 }
      }} ref={contentRef}>
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e8f0'
        }}>
          <Box sx={{ width: '100%', mb: 3 }}>
            <Typography variant="h5" sx={{
              mb: 2,
              color: '#2c5282',
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
              fontWeight: 500
            }}>
              Case Intake Form
            </Typography>

            {/* Only show progress bar when cancer type is selected */}
            {cancerType && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  mb: 2,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4299e1'
                  }
                }}
              />
            )}
          </Box>

          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            {/* Left navigation column - custom two-row display on mobile, vertical on desktop */}
            {cancerType && (
              <Box sx={{
                width: { xs: '100%', sm: '200px' }, // Changed from 200px to 300px
                borderRight: { xs: 'none', sm: '1px solid #eaeaea' },
                pr: { xs: 0, sm: 2 },
                mb: { xs: 2, sm: 0 },
                borderBottom: { xs: '1px solid #eaeaea', sm: 'none' },
                pb: { xs: 2, sm: 0 }
              }}>
                {/* Desktop view uses standard vertical stepper */}
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    sx={{
                      '& .MuiStepConnector-line': {
                        borderColor: '#e2e8f0'
                      },
                      '& .MuiStepLabel-label': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <Step key="cancer-type">
                      <StepLabel
                        onClick={() => handleStepClick(0)}
                        sx={{ cursor: visitedSteps.includes(0) ? 'pointer' : 'default' }}
                        StepIconComponent={({ active }) =>
                          completedSteps.includes(0) ? <CheckCircleIcon color="primary" sx={{ color: '#4299e1' }} /> :
                            active ? <RadioButtonUncheckedIcon color="primary" sx={{ color: '#4299e1' }} /> :
                              <RadioButtonUncheckedIcon color={visitedSteps.includes(0) ? "action" : "disabled"} />
                        }
                      >
                        <Typography
                          sx={{
                            color: visitedSteps.includes(0) ? '#2c5282' : 'text.disabled',
                            textDecoration: visitedSteps.includes(0) ? 'underline' : 'none',
                            cursor: visitedSteps.includes(0) ? 'pointer' : 'default',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            display: isMobile ? 'none' : 'block'
                          }}
                        >
                          Cancer Type
                        </Typography>
                      </StepLabel>
                    </Step>

                    {formSchema && formSchema.map((section, index) => {
                      const stepIndex = index + 1;
                      return (
                        <Step key={section.section}>
                          <StepLabel
                            onClick={() => handleStepClick(stepIndex)}
                            sx={{ cursor: visitedSteps.includes(stepIndex) ? 'pointer' : 'default' }}
                            StepIconComponent={({ active }) =>
                              completedSteps.includes(stepIndex) ? <CheckCircleIcon color="primary" sx={{ color: '#4299e1' }} /> :
                                active ? <RadioButtonUncheckedIcon color="primary" sx={{ color: '#4299e1' }} /> :
                                  <RadioButtonUncheckedIcon color={visitedSteps.includes(stepIndex) ? "action" : "disabled"} />
                            }
                          >
                            <Typography
                              sx={{
                                color: visitedSteps.includes(stepIndex) ? '#2c5282' : 'text.disabled',
                                textDecoration: visitedSteps.includes(stepIndex) ? 'underline' : 'none',
                                cursor: visitedSteps.includes(stepIndex) ? 'pointer' : 'default',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                display: isMobile ? 'none' : 'block'
                              }}
                            >
                              {section.section}
                            </Typography>
                          </StepLabel>
                        </Step>
                      );
                    })}

                    {/* Add Review step */}
                    {formSchema && (
                      <Step key="review">
                        <StepLabel
                          onClick={() => areAllStepsCompleted() ? handleStepClick(formSchema.length + 1) : null}
                          sx={{ cursor: areAllStepsCompleted() ? 'pointer' : 'default' }}
                          StepIconComponent={({ active }) =>
                            active ? <RadioButtonUncheckedIcon color="primary" sx={{ color: '#4299e1' }} /> :
                              <RadioButtonUncheckedIcon color={areAllStepsCompleted() ? "action" : "disabled"} />
                          }
                        >
                          <Typography
                            sx={{
                              color: areAllStepsCompleted() ? '#2c5282' : 'text.disabled',
                              textDecoration: areAllStepsCompleted() ? 'underline' : 'none',
                              cursor: areAllStepsCompleted() ? 'pointer' : 'default',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              display: isMobile ? 'none' : 'block'
                            }}
                          >
                            Review
                          </Typography>
                        </StepLabel>
                      </Step>
                    )}
                  </Stepper>
                </Box>

                {/* Mobile view uses custom two-row display */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <MobileStepper />
                </Box>
              </Box>
            )}

            {/* Form content area */}
            <Box sx={{ flexGrow: 1 }}>
              {getStepContent(activeStep)}

              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    width: { xs: '100%', sm: '120px' }, // Fixed width on desktop
                    color: activeStep === 0 ? '#a0aec0' : '#4299e1',
                    borderColor: activeStep === 0 ? '#e2e8f0' : '#4299e1',
                    height: '42px',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    px: { xs: 2, sm: 3 },
                    '&:hover': {
                      borderColor: '#2c5282',
                      color: '#2c5282'
                    }
                  }}
                >
                  Back
                </Button>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  {isReviewStep ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      fullWidth={isMobile}
                      sx={{
                        bgcolor: '#4299e1',
                        '&:hover': { bgcolor: '#2c5282' },
                        height: '42px',
                        width: { xs: '100%', sm: '120px' }, // Fixed width on desktop
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      Submit
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={activeStep === 0 && !cancerType}
                      fullWidth={isMobile}
                      sx={{
                        bgcolor: '#4299e1',
                        '&:hover': { bgcolor: '#2c5282' },
                        height: '42px',
                        width: { xs: '100%', sm: '120px' }, // Fixed width on desktop
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        opacity: (activeStep === 0 && !cancerType) ? 0.7 : 1
                      }}
                    >
                      {activeStep === formSchema?.length ? "Review" : "Next"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default CaseIntakeForm;